import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { differenceInDays } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Divider } from "@mui/material";

import DateRangeSelect from "components/common/DateRangeSelect";
import Daterange from "components/common/Daterange";

import { getLeaveCountV2 } from "services/useTimeoffService";
import { leaveTypeIds } from "../Enum";

import { isWeekend } from "util/utils";

import "./SwappedLeavesDateSelection.scss";

function SwappedLeavesDateSelection({ onChange, leavesCalendar, validDates, holidaysCalendar }) {
  const { swappedLeavesRevamp } = useFlags();

  const [swapDaysRange, setSwapDaysRange] = useState(null);
  const [timeOffDaysRange, setTimeOffDaysRange] = useState(null);
  const [swapDaysError, setSwapDaysError] = useState("");
  const [timeOffDaysError, setTimeOffDaysError] = useState("");
  const [swapDaysInfo, setSwapDaysInfo] = useState("");
  const [swapDaysCount, setSwapDaysCount] = useState(0);
  const [apiSwapDaysCount, setApiSwapDaysCount] = useState(null);
  const [apiErrorMessage, setApiErrorMessage] = useState("");

  const allHolidays = useSelector((state) => state?.Holidays?.allHolidays);

  const getHolidayName = (day) => {
    const holiday = allHolidays?.data?.find(
      (leave) =>
        moment(day).isSame(moment(leave?.StartDate.split("T")[0]), "day") ||
        (leave.EndDate &&
          moment(day).isBetween(
            moment(leave?.StartDate.split("T")[0]),
            moment(leave?.EndDate.split("T")[0]),
            null,
            "[]"
          ))
    );
    return holiday?.Name || null;
  };

  const isValidSwapDate = (date) => {
    return isWeekend(date) || !!getHolidayName(date);
  };

  const validateSwapDateRange = (startDate, endDate) => {
    const dates = [moment(startDate), moment(endDate)].sort((a, b) => a.diff(b));
    const start = dates[0];
    const end = dates[1];

    if (start.isSame(end, "day")) {
      return { isValid: true, error: "" };
    }

    const current = start.clone();

    while (current.isSameOrBefore(end)) {
      const isValid = isValidSwapDate(current.toDate());

      if (!isValid) {
        return {
          isValid: false,
          error: "Swap days should only include holidays or weekends.",
        };
      }
      current.add(1, "day");
    }

    return { isValid: true, error: "" };
  };

  const getSwapDaysBreakdown = useCallback(
    (startDate, endDate) => {
      if (!startDate || !endDate) return "";

      const start = moment(startDate);
      const end = moment(endDate);
      const current = start.clone();
      const holidays = new Set();
      let weekendDays = 0;

      while (current.isSameOrBefore(end)) {
        const holidayName = getHolidayName(current.toDate());
        if (holidayName) {
          holidays.add(holidayName);
        } else if (isWeekend(current.toDate())) {
          weekendDays += 1;
        }
        current.add(1, "day");
      }

      const labels = [];
      if (holidays.size > 0) {
        labels.push(...Array.from(holidays));
      }
      if (weekendDays > 0) {
        labels.push(`${weekendDays} weekend day${weekendDays > 1 ? "s" : ""}`);
      }

      return labels.length > 0 ? labels.join(", ") : "";
    },
    [allHolidays, getHolidayName]
  );

  const handleSwapDaysChange = (selection) => {
    if (selection && selection.startDate && selection.endDate) {
      const validation = validateSwapDateRange(selection.startDate, selection.endDate);

      if (!validation.isValid) {
        setSwapDaysError(validation.error);
        setSwapDaysRange(null);
        setSwapDaysInfo("");
        setSwapDaysCount(0);
        setTimeOffDaysRange(null);
        setTimeOffDaysError("");
        setApiSwapDaysCount(null);
        setApiErrorMessage("");
      } else {
        setSwapDaysError("");
        setSwapDaysRange(selection);
        const breakdown = getSwapDaysBreakdown(selection.startDate, selection.endDate);
        setSwapDaysInfo(breakdown);

        const daysCount = differenceInDays(new Date(selection.endDate), new Date(selection.startDate)) + 1;
        setSwapDaysCount(daysCount);

        if (timeOffDaysRange) {
          validateTimeOffDays(timeOffDaysRange, selection);
        } else {
          setApiSwapDaysCount(null);
          setApiErrorMessage("");
        }
      }
    } else {
      setSwapDaysRange(null);
      setSwapDaysError("");
      setSwapDaysInfo("");
      setSwapDaysCount(0);
      setTimeOffDaysRange(null);
      setTimeOffDaysError("");
      setApiSwapDaysCount(null);
      setApiErrorMessage("");
    }
  };

  const validateTimeOffDays = async (timeOffSelection, swapSelection = swapDaysRange) => {
    if (!timeOffSelection || !timeOffSelection.startDate || !timeOffSelection.endDate) return;
    if (!swapSelection) {
      setTimeOffDaysError("Please select swap days first");
      return;
    }

    setTimeOffDaysError("");
    setApiErrorMessage("");

    try {
      const leaveCount = await getLeaveCountV2(
        timeOffSelection.startDate,
        timeOffSelection.endDate,
        leaveTypeIds.Swapped,
        "Full Day",
        swapSelection.startDate,
        swapSelection.endDate,
        swappedLeavesRevamp
      );

      if (leaveCount?.status === 200 && leaveCount?.payload?.success) {
        const availableLeaves = leaveCount.payload.leaves || 0;
        setApiSwapDaysCount(availableLeaves);
        const requestedDays =
          differenceInDays(new Date(timeOffSelection.endDate), new Date(timeOffSelection.startDate)) + 1;

        if (requestedDays > availableLeaves) {
          setTimeOffDaysError(
            `You can only select up to ${availableLeaves} time-off day${availableLeaves !== 1 ? "s" : ""} based on your selected swap days.`
          );
          setTimeOffDaysRange(null);
        } else {
          setTimeOffDaysError("");
          setApiErrorMessage("");
        }
      } else {
        setApiErrorMessage(leaveCount?.payload?.message || "Error validating time-off days");
        setTimeOffDaysError("");
      }
    } catch (error) {
      setApiErrorMessage("Error validating time-off days. Please try again.");
    }
  };

  const handleTimeOffDaysChange = async (selection) => {
    if (selection && selection.startDate && selection.endDate) {
      setTimeOffDaysRange(selection);
      await validateTimeOffDays(selection);
    }
  };

  const formatDateRange = (startDate, endDate, format = "MMM DD, YYYY") => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (start.isSame(end, "day")) {
      return start.format(format);
    }

    return `${start.format(format)} - ${end.format(format)}`;
  };

  useEffect(() => {
    if (swapDaysRange && timeOffDaysRange && onChange) {
      onChange({
        swapDays: swapDaysRange,
        timeOffDays: timeOffDaysRange,
        swapDaysInfo,
        swapDaysBreakdown: getSwapDaysBreakdown(swapDaysRange.startDate, swapDaysRange.endDate),
        isValid: !swapDaysError && !timeOffDaysError && !apiErrorMessage,
      });
    } else if (onChange) {
      onChange({
        swapDays: swapDaysRange,
        timeOffDays: timeOffDaysRange,
        swapDaysInfo,
        swapDaysBreakdown: swapDaysRange ? getSwapDaysBreakdown(swapDaysRange.startDate, swapDaysRange.endDate) : "",
        isValid: false,
      });
    }
  }, [swapDaysRange, timeOffDaysRange, swapDaysInfo, swapDaysError, timeOffDaysError, apiErrorMessage]);

  return (
    <div className="swapped-leaves-date-selection">
      <Divider light />

      <div className="date-selection-container">
        <div className="date-selection-row">
          <div className="date-select-field">
            <DateRangeSelect
              name="swapDays"
              label="Select holiday(s) or weekend(s)"
              placeholder="Select"
              value={swapDaysRange}
              onChange={handleSwapDaysChange}
              error={!!swapDaysError}
              helperText={swapDaysError}
              dateRangeComponent={
                <Daterange
                  months={2}
                  leavesCalender={leavesCalendar}
                  holidaysCalender={holidaysCalendar}
                  isSwappedLeaves
                  validDates={validDates}
                  disabledDate={(date) => !isValidSwapDate(date)}
                  initialDateRange={swapDaysRange}
                />
              }
            />
            {apiSwapDaysCount !== null && (
              <p className="para-body-s-regular swap-days-count">
                {apiSwapDaysCount} day{apiSwapDaysCount !== 1 ? "s" : ""} to swap
              </p>
            )}
          </div>

          <div className="date-select-field">
            <DateRangeSelect
              name="timeOffDays"
              label="Select time-off day(s)"
              placeholder="Select"
              value={timeOffDaysRange}
              onChange={handleTimeOffDaysChange}
              error={!!timeOffDaysError}
              helperText={timeOffDaysError}
              disabled={!swapDaysRange || swapDaysCount === 0}
              dateRangeComponent={
                <Daterange
                  months={2}
                  leavesCalender={leavesCalendar}
                  holidaysCalender={holidaysCalendar}
                  isSwappedLeaves={false}
                  validDates={validDates}
                  disableHolidays
                  initialDateRange={timeOffDaysRange}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* Time-off selected section */}
      {timeOffDaysRange && apiSwapDaysCount !== null && (
        <>
          <Divider light className="mt-1" />
          <div className="time-off-selected-section">
            <p className="para-body-s-regular time-off-label">Time-off selected</p>
            <div className="time-off-content">
              <p className="para-body-m-medium time-off-title">
                {apiSwapDaysCount} Swapped leave{apiSwapDaysCount !== 1 ? "s" : ""}
              </p>
              <p className="heading-h5-semibold time-off-dates">
                {formatDateRange(timeOffDaysRange.startDate, timeOffDaysRange.endDate)}
              </p>
              <p className="para-body-s-regular swap-info">
                Swap with: {formatDateRange(swapDaysRange.startDate, swapDaysRange.endDate)}
                {swapDaysInfo && ` (${swapDaysInfo})`}
              </p>
            </div>
          </div>
        </>
      )}

      {apiErrorMessage && (
        <div className="api-error-message">
          <p className="para-body-s-regular error-text">{apiErrorMessage}</p>
        </div>
      )}
    </div>
  );
}

export default SwappedLeavesDateSelection;
